/**
 * DrawdownEmailsTab — Phase C
 * Lets the user pick an APPROVED template (drawdown_investor context),
 * select investors, and bulk-send notice emails as a background job.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Mail, Send, Users, FileText, AlertCircle, CheckCircle, Loader2, X, StopCircle,
  Clock, History, Paperclip, RefreshCw, ChevronDown, ChevronRight, Eye, MessageCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import EmailPreviewModal from '../../components/email/EmailPreviewModal';
import TeamMemberPicker from '../../components/email/TeamMemberPicker';
import RecipientSearchBar, { filterByQuery } from '../../components/email/RecipientSearchBar';
import useWhatsAppSend from '../../hooks/useWhatsAppSend';
import WhatsAppSendOptions from '../../components/whatsapp/WhatsAppSendOptions';
import WhatsAppSendResultModal from '../../components/whatsapp/WhatsAppSendResultModal';
import WhatsAppSendHistory from '../../components/whatsapp/WhatsAppSendHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:    { cls: 'bg-gray-100 text-gray-700',   label: 'Pending' },
  running:    { cls: 'bg-blue-100 text-blue-700',   label: 'Sending...' },
  completed:  { cls: 'bg-green-100 text-green-800', label: 'Completed' },
  aborted:    { cls: 'bg-yellow-100 text-yellow-800', label: 'Aborted' },
  failed:     { cls: 'bg-red-100 text-red-800',     label: 'Failed' },
};

const REC_STATUS_META = {
  pending: { cls: 'bg-gray-100 text-gray-600', label: 'Pending' },
  sent:    { cls: 'bg-green-100 text-green-700', label: 'Sent' },
  failed:  { cls: 'bg-red-100 text-red-700', label: 'Failed' },
  skipped: { cls: 'bg-yellow-100 text-yellow-700', label: 'Skipped' },
  aborted: { cls: 'bg-yellow-100 text-yellow-700', label: 'Aborted' },
};

const DrawdownEmailsTab = ({ drawdownId, investors = [], currentUser }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [includeNotice, setIncludeNotice] = useState(true);
  const [attachProtected, setAttachProtected] = useState(true);  // true => password-protected PDF, false => unprotected
  const [bccSelf, setBccSelf] = useState(false);
  // Email send is opt-in/out so admins can re-attempt a WhatsApp-only blast
  // without re-sending the email. Default to true to preserve historic flow.
  const [sendEmail, setSendEmail] = useState(true);
  const [dateFormat, setDateFormat] = useState('dd mmmm yyyy');
  const [numberFormat, setNumberFormat] = useState('western');
  const [selectedInvestorIds, setSelectedInvestorIds] = useState([]);
  const [consolidateByAccount, setConsolidateByAccount] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [activeJob, setActiveJob] = useState(null); // currently-polling job
  const [history, setHistory] = useState([]);
  const [expandedJob, setExpandedJob] = useState(null); // job id whose recipients are expanded
  const [previewOpen, setPreviewOpen] = useState(false);
  const [ccUserIds, setCcUserIds] = useState([]);
  const [bccUserIds, setBccUserIds] = useState([]);

  // -------- WhatsApp (Phase 2A) — backed by shared hook --------
  const wa = useWhatsAppSend(drawdownId, 'emails');

  useEffect(() => {
    loadTemplates();
    loadHistory();
    // eslint-disable-next-line
  }, [drawdownId]);

  // Poll active job
  useEffect(() => {
    if (!activeJob || activeJob.status === 'completed' || activeJob.status === 'aborted' || activeJob.status === 'failed') return;
    const tick = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (data.status === 'completed' || data.status === 'aborted' || data.status === 'failed') {
          loadHistory();
        }
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [activeJob?.id, activeJob?.status]);

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/email-templates?scope=approved&section=emails`);
      // Phase C is drawdown-focused → prefer drawdown_investor + general
      const allowed = (data || []).filter(t => ['drawdown_investor', 'general'].includes(t.context_type));
      setTemplates(allowed);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load approved templates');
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/history`);
      setHistory((data || []).filter(j => j.kind === 'drawdown_notice'));
    } catch { /* ignore */ }
  };

  const toggleInvestor = (id) => {
    setSelectedInvestorIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  // Search-aware "select all" — only sweeps the rows currently visible
  // through the search filter, so the user can't accidentally select
  // hidden investors.
  const [recipientSearch, setRecipientSearch] = useState('');
  const visibleInvestors = useMemo(
    () => filterByQuery(investors, recipientSearch,
      ['account_name', 'class_name', 'folio_no', 'pan']),
    [investors, recipientSearch]
  );
  const toggleAll = () => {
    const ids = visibleInvestors.map(i => i.id);
    const allOn = ids.length > 0 && ids.every(id => selectedInvestorIds.includes(id));
    if (allOn) setSelectedInvestorIds(prev => prev.filter(id => !ids.includes(id)));
    else setSelectedInvestorIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const handleSend = async () => {
    setError(''); setSuccess('');
    if (!selectedTemplate) { setError('Pick an approved template first.'); return; }
    if (selectedInvestorIds.length === 0) { setError('Select at least one investor.'); return; }
    // Email + WhatsApp can each be toggled independently — disallow the case
    // where neither is enabled so the admin always knows something was sent.
    if (!sendEmail && !wa.canSend) {
      setError('Nothing to send — enable the email checkbox or the "Also send WhatsApp" toggle.');
      return;
    }
    try {
      let emailQueued = 0;
      if (sendEmail) {
        const { data } = await axios.post(`${API}/drawdowns/${drawdownId}/emails/send`, {
          template_id: selectedTemplate,
          drawdown_investor_ids: selectedInvestorIds,
          include_drawdown_notice: includeNotice,
          attach_protected: attachProtected,
          bcc_self: bccSelf,
          date_format: dateFormat,
          number_format: numberFormat,
          cc_user_ids: ccUserIds,
          bcc_user_ids: bccUserIds,
          consolidate_by_account: consolidateByAccount,
        });
        // Fetch full job so we can render progress
        const { data: job } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/jobs/${data.job_id}`);
        setActiveJob(job);
        emailQueued = data.total;
      }

      // Phase 2A: optionally also fire off a WhatsApp send for the same selection.
      if (wa.canSend) {
        const res = await wa.triggerSend({
          drawdownInvestorIds: selectedInvestorIds,
          preferProtected: attachProtected,
          dateFormat,
          numberFormat,
        });
        if (!res.ok) {
          setError(sendEmail
            ? `Email queued OK, but WhatsApp send failed: ${res.error}`
            : `WhatsApp send failed: ${res.error}`);
          return;
        }
      }
      if (sendEmail && wa.canSend) {
        setSuccess(`Queued ${emailQueued} email(s) and a parallel WhatsApp send. Watching progress below...`);
      } else if (sendEmail) {
        setSuccess(`Queued ${emailQueued} email(s). Watching progress below...`);
      } else {
        setSuccess('WhatsApp send queued — see the progress modal for live status.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start send');
    }
  };

  const handleAbort = async () => {
    if (!activeJob) return;
    if (!window.confirm(`Abort this send? Already-sent emails cannot be unsent — only pending recipients will be skipped.`)) return;
    try {
      await axios.post(`${API}/drawdowns/${drawdownId}/emails/jobs/${activeJob.id}/abort`);
      setSuccess('Abort requested. Stopping after the current recipient...');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to abort'); }
  };

  const expandJob = async (jobId) => {
    if (expandedJob === jobId) { setExpandedJob(null); return; }
    try {
      const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/jobs/${jobId}`);
      setExpandedJob(jobId);
      // shove full job data into history
      setHistory(prev => prev.map(j => j.id === jobId ? { ...j, recipients: data.recipients } : j));
    } catch { /* ignore */ }
  };

  const senderEnabled = !!currentUser?.email_sending_enabled;
  const totalSelected = selectedInvestorIds.length;

  const sendingActive = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');
  const progressPct = activeJob && activeJob.total > 0
    ? Math.round(((activeJob.sent || 0) + (activeJob.failed || 0) + (activeJob.skipped || 0)) / activeJob.total * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="dd-emails-tab">
      {!senderEnabled && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-testid="dd-emails-disabled-notice">
          <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold">You can't send emails from your account yet.</p>
            <p className="mt-1">Ask an Admin to enable "Allow this user to send emails" for you in <strong>User Management</strong>, then save your SMTP credentials in <Link className="underline text-blue-700" to="/email-config">Email Configuration</Link>.</p>
          </div>
        </div>
      )}

      {/* Send composer */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-900 flex items-center mb-4">
          <Send size={16} className="mr-2 text-blue-600" /> Send Drawdown Notice Emails
        </h3>

        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800 flex items-start" data-testid="dd-emails-error">
            <AlertCircle size={14} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center" data-testid="dd-emails-success">
            <CheckCircle size={14} className="text-green-600 mr-2" /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Approved Template *</label>
            <select
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-emails-template-select"
            >
              <option value="">— Pick a template —</option>
              {templates.length === 0 && <option disabled>(no approved templates yet)</option>}
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} · {t.context_type === 'drawdown_investor' ? 'Drawdown' : 'General'}</option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Need to <Link className="text-blue-600 underline" to="/email-templates/new">create one</Link> first and have an Admin approve it.
              </p>
            )}
          </div>
          <div className="space-y-2 pt-1">
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" checked={includeNotice} onChange={e => setIncludeNotice(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" data-testid="dd-emails-include-notice" />
              <span className="ml-2 text-sm text-gray-700"><Paperclip size={12} className="inline mr-1" />Auto-attach the reviewed drawdown notice (PDF) for each investor</span>
            </label>
            {includeNotice && (
              <div className="ml-6 mt-1 flex flex-wrap items-center gap-4 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-testid="dd-emails-protect-choice">
                <span className="font-medium text-gray-600">Attach as:</span>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio" name="dd-emails-attach-protected"
                    checked={attachProtected === true}
                    onChange={() => setAttachProtected(true)}
                    className="h-3.5 w-3.5 text-blue-600"
                    data-testid="dd-emails-attach-protected-yes"
                  />
                  <span className="ml-1.5">Password-protected PDF (recommended)</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio" name="dd-emails-attach-protected"
                    checked={attachProtected === false}
                    onChange={() => setAttachProtected(false)}
                    className="h-3.5 w-3.5 text-blue-600"
                    data-testid="dd-emails-attach-protected-no"
                  />
                  <span className="ml-1.5">Unprotected PDF (no password)</span>
                </label>
                <span className="text-[11px] text-gray-500 ml-1">If a protected copy doesn&apos;t exist, the unprotected version is sent regardless.</span>
              </div>
            )}
            <label className="flex items-start cursor-pointer" data-testid="dd-emails-send-email-toggle">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={e => setSendEmail(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded mt-0.5"
              />
              <span className="ml-2 text-sm text-gray-700">
                <Mail size={12} className="inline mr-1 text-blue-600" />
                Send email
                <span className="text-gray-500"> — uncheck if you only want to re-send the WhatsApp message (e.g. retrying after a phone-number fix).</span>
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" checked={bccSelf} onChange={e => setBccSelf(e.target.checked)} disabled={!sendEmail} className={`h-4 w-4 text-blue-600 rounded ${!sendEmail ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="dd-emails-bcc-self" />
              <span className={`ml-2 text-sm ${sendEmail ? 'text-gray-700' : 'text-gray-400'}`}>BCC me on every email</span>
            </label>
            {investors.some(i => i.class_name) && (
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={consolidateByAccount}
                  onChange={e => setConsolidateByAccount(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded mt-0.5"
                  data-testid="dd-emails-consolidate-account"
                />
                <span className="ml-2 text-sm text-gray-700">
                  <Users size={12} className="inline mr-1" />
                  Send a single email per account
                  <span className="text-gray-500"> — combines every class for the same investor into one email and attaches all the class PDFs.</span>
                </span>
              </label>
            )}
            {/* WhatsApp send affordance — promoted to a first-class panel with
                its own template picker (mirrors the email Approved Template
                selector). Powered by the shared hook + component so the same
                UX is reused by the Reminders + Acknowledgments tabs. */}
            <WhatsAppSendOptions
              wa={wa}
              label="fires a parallel WhatsApp send over the same selected recipients."
            />
          </div>
        </div>

        {/* Formatting options — apply to body merge fields and the receipts table */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date Format
              <span className="text-gray-400 ml-1">(Receipt Date & date merge fields)</span>
            </label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-emails-date-format-select">
              <option value="dd mmmm yyyy">25 January 2026</option>
              <option value="dd-mm-yyyy">25-01-2026</option>
              <option value="mm-dd-yyyy">01-25-2026</option>
              <option value="dd/mm/yyyy">25/01/2026</option>
              <option value="mm/dd/yyyy">01/25/2026</option>
              <option value="yyyy-mm-dd">2026-01-25</option>
              <option value="mmmm dd, yyyy">January 25, 2026</option>
              <option value="dd-mmmm-yyyy">25-January-2026</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Number Format
              <span className="text-gray-400 ml-1">(amount columns & number merge fields)</span>
            </label>
            <select value={numberFormat} onChange={e => setNumberFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-emails-number-format-select">
              <option value="western">Western (1,000,000)</option>
              <option value="indian">Indian (10,00,000)</option>
            </select>
          </div>
        </div>

        {/* Team Member Cc / Bcc — copies AMC teammates on every recipient */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <TeamMemberPicker
            label="CC team members"
            value={ccUserIds}
            onChange={setCcUserIds}
            placeholder="Add teammates to Cc on every email…"
            testid="dd-emails-cc-team"
          />
          <TeamMemberPicker
            label="BCC team members"
            value={bccUserIds}
            onChange={setBccUserIds}
            placeholder="Add teammates to Bcc on every email…"
            testid="dd-emails-bcc-team"
          />
        </div>

        {/* Investor selector */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-semibold text-gray-700 flex items-center"><Users size={12} className="mr-1" /> Recipients ({totalSelected} of {investors.length})</span>
            <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-[400px]">
              <RecipientSearchBar value={recipientSearch} onChange={setRecipientSearch} placeholder="Search investor, PAN, class, folio…" testid="dd-emails-recipient-search" className="flex-1" />
              <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline disabled:opacity-50 whitespace-nowrap" data-testid="dd-emails-select-all" disabled={visibleInvestors.length === 0}>
                {visibleInvestors.length > 0 && visibleInvestors.every(i => selectedInvestorIds.includes(i.id)) ? 'Clear visible' : 'Select all visible'}
              </button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {investors.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 italic text-center">No investors on this drawdown yet.</p>
            ) : visibleInvestors.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 italic text-center" data-testid="dd-emails-recipients-empty">No investors match "{recipientSearch}".</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {visibleInvestors.map(inv => (
                    <tr key={inv.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={selectedInvestorIds.includes(inv.id)}
                          onChange={() => toggleInvestor(inv.id)}
                          data-testid={`dd-emails-pick-${inv.id}`}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{inv.account_name}</div>
                        <div className="text-xs text-gray-500">
                          {inv.class_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded mr-1">{inv.class_name}</span>}
                          {inv.ppm_no && <span>PPM: {inv.ppm_no} · </span>}
                          {inv.folio_no && <span>Folio: {inv.folio_no}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 text-right">
                        Drawdown: <span className="font-mono">{(inv.drawdown_amount || 0).toLocaleString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={!selectedTemplate || totalSelected === 0}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dd-emails-preview-btn"
            title="Preview the email for one selected investor"
          >
            <Eye size={14} className="mr-2" /> Preview
          </button>
          <button
            onClick={handleSend}
            disabled={!senderEnabled || sendingActive || templates.length === 0 || totalSelected === 0 || !selectedTemplate}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dd-emails-send-btn"
          >
            <Send size={14} className="mr-2" /> Send to {totalSelected || '...'} Investor{totalSelected === 1 ? '' : 's'}
          </button>
        </div>
      </div>

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        drawdownId={drawdownId}
        templateId={selectedTemplate}
        kind="drawdown_notice"
        recipients={investors
          .filter(i => selectedInvestorIds.includes(i.id))
          .map(i => ({ id: i.id, account_name: i.account_name, class_name: i.class_name, folio_no: i.folio_no }))}
        includeDrawdownNotice={includeNotice}
        bccSelf={bccSelf}
        dateFormat={dateFormat}
        numberFormat={numberFormat}
        ccUserIds={ccUserIds}
        bccUserIds={bccUserIds}
        extraPayload={{ attach_protected: attachProtected }}
      />

      {/* WhatsApp send result modal */}
      {wa.resultOpen && wa.activeJob && (
        <WhatsAppSendResultModal
          job={wa.activeJob}
          onClose={() => wa.setResultOpen(false)}
        />
      )}

      {/* Persistent WhatsApp send history — surfaces past job outcomes
          (including failed deliveries) after the in-flight modal closes.
          Refreshes when a new job completes via the `wa.activeJob.status`
          terminal state (completed / failed / aborted). */}
      <WhatsAppSendHistory
        drawdownId={drawdownId}
        refreshKey={(wa.activeJob && ['completed','failed','aborted'].includes(wa.activeJob.status)) ? wa.activeJob.id : 0}
      />

      {/* Active job progress */}
      {activeJob && (
        <div className="bg-white border border-blue-300 rounded-lg p-5" data-testid="dd-emails-active-job">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-900 flex items-center">
              <Loader2 size={14} className={`mr-2 ${sendingActive ? 'animate-spin text-blue-600' : 'text-gray-400'}`} />
              {sendingActive ? 'Sending in progress' : 'Last send result'}
            </h3>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${(STATUS_META[activeJob.status] || STATUS_META.pending).cls}`} data-testid="dd-emails-job-status">
                {(STATUS_META[activeJob.status] || STATUS_META.pending).label}
              </span>
              {sendingActive && (
                <button onClick={handleAbort} className="text-xs flex items-center bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100" data-testid="dd-emails-abort-btn">
                  <StopCircle size={12} className="mr-1" /> Abort
                </button>
              )}
              <button onClick={() => setActiveJob(null)} className="text-gray-400 hover:text-gray-700" title="Dismiss"><X size={14} /></button>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="grid grid-cols-4 gap-2 text-center text-xs mb-3">
            <div className="bg-gray-50 rounded p-2"><p className="font-bold text-gray-900">{activeJob.total || 0}</p><p className="text-gray-500">Total</p></div>
            <div className="bg-green-50 rounded p-2"><p className="font-bold text-green-700">{activeJob.sent || 0}</p><p className="text-gray-500">Sent</p></div>
            <div className="bg-red-50 rounded p-2"><p className="font-bold text-red-700">{activeJob.failed || 0}</p><p className="text-gray-500">Failed</p></div>
            <div className="bg-yellow-50 rounded p-2"><p className="font-bold text-yellow-700">{activeJob.skipped || 0}</p><p className="text-gray-500">Skipped</p></div>
          </div>
          <div className="max-h-56 overflow-y-auto border border-gray-100 rounded">
            <table className="w-full text-xs">
              <tbody>
                {(activeJob.recipients || []).map((r, i) => {
                  const meta = REC_STATUS_META[r.status] || REC_STATUS_META.pending;
                  return (
                    <tr key={`${r.drawdown_investor_id}-${i}`} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-1.5 truncate max-w-xs">{r.account_name}</td>
                      <td className="px-3 py-1.5 text-gray-500 text-[11px]">
                        <div><span className="font-semibold text-gray-600">To:</span> {(r.to_emails || []).join(', ') || <em>—</em>}</div>
                        {(r.cc_emails || []).length > 0 && (
                          <div><span className="font-semibold text-gray-600">Cc:</span> {(r.cc_emails || []).join(', ')}</div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="px-3 py-1.5 text-red-600 text-[11px] max-w-[16rem] truncate" title={r.error || ''}>{r.error || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900 flex items-center"><History size={14} className="mr-2 text-gray-600" /> Send History</h3>
          <button onClick={loadHistory} className="text-xs text-gray-500 hover:text-gray-800 flex items-center" data-testid="dd-emails-refresh-history"><RefreshCw size={12} className="mr-1" /> Refresh</button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500 italic text-center py-4">No emails have been sent for this drawdown yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map(job => {
              const meta = STATUS_META[job.status] || STATUS_META.pending;
              const expanded = expandedJob === job.id;
              return (
                <div key={job.id} className="border border-gray-200 rounded" data-testid={`dd-emails-job-${job.id}`}>
                  <button onClick={() => expandJob(job.id)} className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left">
                    <div className="flex items-center gap-3">
                      {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span className="text-xs text-gray-500"><Clock size={11} className="inline mr-1" />{new Date(job.created_at).toLocaleString()}</span>
                      <span className="text-sm font-medium text-gray-900">{job.template_name}</span>
                      <span className="text-xs text-gray-500">by {job.user_name || job.user_email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${meta.cls}`}>{meta.label}</span>
                      <span className="text-green-700 font-mono">{job.sent || 0}/{job.total}</span>
                      {(job.failed > 0) && <span className="text-red-700 font-mono">✕ {job.failed}</span>}
                    </div>
                  </button>
                  {expanded && job.recipients && (
                    <div className="border-t border-gray-100 max-h-56 overflow-y-auto">
                      <table className="w-full text-xs">
                        <tbody>
                          {job.recipients.map((r, i) => {
                            const rmeta = REC_STATUS_META[r.status] || REC_STATUS_META.pending;
                            return (
                              <tr key={`${r.drawdown_investor_id}-${i}`} className="border-b border-gray-50 last:border-0">
                                <td className="px-3 py-1.5">{r.account_name}</td>
                                <td className="px-3 py-1.5 text-gray-500 text-[11px]">
                                  <div><span className="font-semibold text-gray-600">To:</span> {(r.to_emails || []).join(', ') || <em>—</em>}</div>
                                  {(r.cc_emails || []).length > 0 && (
                                    <div><span className="font-semibold text-gray-600">Cc:</span> {(r.cc_emails || []).join(', ')}</div>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-right"><span className={`px-2 py-0.5 rounded-full font-medium ${rmeta.cls}`}>{rmeta.label}</span></td>
                                <td className="px-3 py-1.5 text-red-600 text-[11px] max-w-[14rem] truncate" title={r.error || ''}>{r.error || ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DrawdownEmailsTab;

